/**
 * Data formatting utilities
 */

/**
 * Sanitizes value - converts null/undefined to empty string
 * @param {any} value - Value to sanitize
 * @returns {any} - Sanitized value
 */
export const sanitizeValue = (value) => {
  return value === null || value === undefined ? "" : value;
};

/**
 * Converts value to null if empty or invalid
 * @param {any} val - Value to check
 * @returns {any|null} - Value or null
 */
export const toNull = (val) => {
  if (
    val === undefined ||
    val === null ||
    val === "" ||
    val === "undefined" ||
    val === "null"
  ) {
    return null;
  }
  
  if (typeof val === "object" && val !== null) {
    if (Array.isArray(val)) {
      return val.length === 0 ? null : val;
    }
    if (Object.keys(val).length === 0) {
      return null;
    }
  }
  
  return val;
};

/**
 * Formats value for address fields
 * @param {any} val - Value to format
 * @returns {any|null} - Formatted value
 */
export const toAddressValue = (val) => {
  if (
    val === undefined ||
    val === null ||
    val === "undefined" ||
    val === "null"
  ) {
    return null;
  }
  return val;
};

/**
 * Trims whitespace from string values in an object
 * @param {Object} obj - Object to trim
 * @returns {Object} - Object with trimmed values
 */
export const trimObjectValues = (obj) => {
  const trimmed = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      trimmed[key] = value.trim();
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      trimmed[key] = trimObjectValues(value);
    } else {
      trimmed[key] = value;
    }
  }
  
  return trimmed;
};

/**
 * Removes empty fields from object
 * @param {Object} obj - Object to clean
 * @returns {Object} - Cleaned object
 */
export const removeEmptyFields = (obj) => {
  const cleaned = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value === 'object' && !Array.isArray(value)) {
        cleaned[key] = removeEmptyFields(value);
      } else {
        cleaned[key] = value;
      }
    }
  }
  
  return cleaned;
};

/**
 * Deep clones an object
 * @param {Object} obj - Object to clone
 * @returns {Object} - Cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj);
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  
  const cloned = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  
  return cloned;
};
